import { Button } from "@/components/ui/button";
import { Eye, Download, Share2, History, Star, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState } from "react";
import type { Document } from "../DocumentCard";

interface DocumentCardActionsProps {
  document: Document;
  isFavorite: boolean;
  onView: (doc: Document) => void;
  onDownload: (doc: Document) => void;
  onShare?: (doc: Document) => void;
  onVersionHistory?: (doc: Document) => void;
  onDelete: (id: string) => void;
  toggleFavorite: (e: React.MouseEvent) => void;
}

export function DocumentCardActions({
  document,
  isFavorite,
  onView,
  onDownload,
  onShare,
  onVersionHistory,
  onDelete,
  toggleFavorite
}: DocumentCardActionsProps) {
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(false);

  const actions = [
    {
      icon: Star,
      label: "Favoritt",
      onClick: toggleFavorite,
      className: isFavorite ? "text-yellow-500" : "text-muted-foreground",
      tooltip: isFavorite ? "Fjern fra favoritter" : "Legg til i favoritter",
      primary: true
    },
    {
      icon: Eye,
      label: "Vis dokument",
      onClick: () => onView(document),
      className: "text-primary",
      tooltip: "Vis dokument",
      primary: true
    },
    ...(onVersionHistory ? [{
      icon: History,
      label: "Versjonshistorikk",
      onClick: () => onVersionHistory(document),
      className: "text-primary",
      tooltip: "Se versjonshistorikk",
      primary: false
    }] : []),
    ...(onShare ? [{
      icon: Share2,
      label: "Del",
      onClick: () => onShare(document),
      className: "text-primary",
      tooltip: "Del dokument",
      primary: false
    }] : []),
    {
      icon: Download,
      label: "Last ned",
      onClick: () => onDownload(document),
      className: "text-primary",
      tooltip: "Last ned dokument",
      primary: false
    },
    {
      icon: Trash2,
      label: "Slett",
      onClick: () => onDelete(document.id),
      className: "text-destructive",
      tooltip: "Slett dokument",
      primary: false
    }
  ];

  const primaryActions = actions.filter(action => action.primary);
  const secondaryActions = actions.filter(action => !action.primary);

  if (isMobile) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          {primaryActions.map(({ icon: Icon, label, onClick, className }) => (
            <Button
              key={label}
              variant="outline"
              size="sm"
              onClick={onClick}
              className={`${className} flex-1`}
            >
              <Icon className="h-4 w-4 mr-2" />
              {label}
            </Button>
          ))}
        </div>
        
        {secondaryActions.length > 0 && (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full flex items-center justify-between"
            >
              {isExpanded ? "Skjul alternativer" : "Vis flere alternativer"}
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 ml-2" />
              ) : (
                <ChevronDown className="h-4 w-4 ml-2" />
              )}
            </Button>

            {isExpanded && (
              <div className="flex flex-col gap-2">
                {secondaryActions.map(({ icon: Icon, label, onClick, className }) => (
                  <Button
                    key={label}
                    variant="ghost"
                    size="sm"
                    onClick={onClick}
                    className={`${className} w-full justify-start`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {label}
                  </Button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex gap-2">
        {actions.map(({ icon: Icon, label, onClick, className, tooltip }) => (
          <Tooltip key={label}>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={onClick}
                className={className}
              >
                <Icon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}